import { Component } from 'preact';

import { scaleLinear } from 'd3-scale';
import { range } from 'd3-array';
import { axisLeft } from 'd3-axis';
import { select } from 'd3-selection';
import style from './style.scss';

/**
 * The Thermometer component renders an SVG thermometer.
 *
 * props:
 * - minValue: thermometer minimum value
 * - maxValue: thermometer maximum value
 * - currentValue: thermometer current value
 */

class Thermometer extends Component {

	constructor(props) {
		super(props);

		this.width = 80;
		this.height = 160;
		this.tubeWidth = 18.5;
		this.margin = 5;
		this.bulbRadius = 18;
	}

	render(props) {
		this.initAxis(props);

		return (
			<svg viewBox={`0 0 ${this.width} ${this.height}`}>

				<defs>
					<radialGradient id="bulb-gradient" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
						<stop offset="0%" class={style.bulb_gradient_stop_1} />
						<stop offset="80%" class={style.bulb_gradient_stop_2} />
					</radialGradient>
				</defs>

				<g stroke-width="1px" transform={`translate(${0.5 * this.width},0)`}>
					{this.renderTube()}
					{this.renderExtremes(props)}
					{this.renderMercury(props)}
					{this.renderScale()}
				</g>

			</svg>
		);
	}

	renderTube() {
		return <g>
			<circle class={style.thermometer_outline}
					r={0.5 * this.tubeWidth}
					cx={0}
					cy={this.margin + 0.5 * this.tubeWidth}
			/>

			<rect class={style.thermometer_outline}
				  x={-0.5 * this.tubeWidth}
				  y={this.margin + 0.5 * this.tubeWidth}
				  width={this.tubeWidth}
				  height={this.height - 2 * this.bulbRadius}
			/>

			<circle class={style.thermometer_empty}
					r={0.5 * this.tubeWidth - 0.5}
					cx={0}
					cy={this.margin + 0.5 * this.tubeWidth}
			/>

			<circle class={style.thermometer_outline}
					r={this.bulbRadius}
					cx={0}
					cy={this.height - this.margin - this.bulbRadius}
			/>

			<rect class={style.thermometer_empty}
				  x={-0.5 * this.tubeWidth + 0.5}
				  y={this.margin + 0.5 * this.tubeWidth}
				  width={this.tubeWidth - 1}
				  height={this.height - 2 * this.bulbRadius}
			/>
		</g>;
	}

	renderExtremes(props) {
		return <g class={style.thermometer_extremes}>

			<line x1={-0.5 * this.tubeWidth}
				  x2={0.5 * this.tubeWidth + 22}
				  y1={this.axisScale(props.minValue)}
				  y2={this.axisScale(props.minValue)}
			/>

			<line x1={-0.5 * this.tubeWidth}
				  x2={0.5 * this.tubeWidth + 22}
				  y1={this.axisScale(props.maxValue)}
				  y2={this.axisScale(props.maxValue)}
			/>

			<text class={style.thermometer_max_label}
				  x={0.5 * this.tubeWidth + 2}
				  y={this.axisScale(props.maxValue)}
				  dy="-0.35em"
			>max</text>

			<text class={style.thermometer_min_label}
				  x={0.5 * this.tubeWidth + 2}
				  y={this.axisScale(props.minValue)}
				  dy="1em"
			>min</text>

		</g>;
	}

	renderMercury(props) {
		const y = this.axisScale(props.currentValue);

		return <g class={style.thermometer_mercury}>
			<rect x={-0.5 * this.tubeWidth + 4}
				  y={y}
				  width={this.tubeWidth - 8}
				  height={this.height - this.margin - this.bulbRadius - y}
			/>
			<circle cx={0}
					cy={this.height - this.margin - this.bulbRadius}
					r={this.bulbRadius - 4}
					fill="url(#bulb-gradient)"
			/>
		</g>;
	}

	renderScale() {
		// Values to use along the scale ticks up the thermometer
		const axisTickValues = range((this.axisDomain[1] - this.axisDomain[0]) / this.axisStep + 1)
			.map(v => this.axisDomain[0] + v * this.axisStep);

		const axis = axisLeft()
			.scale(this.axisScale)
			.tickSizeInner(7)
			.tickSizeOuter(0)
			.tickValues(axisTickValues);

		return <g class={style.thermometer_scale}
				  transform={`translate(${-0.5 * this.tubeWidth},0)`}
				  ref={elem => select(elem).call(axis)}
		/>;
	}

	initAxis(props) {
		this.axisStep = 5;

		// Determine a suitable range of the temperature scale
		this.axisDomain = [
			this.axisStep * Math.floor(props.minValue / this.axisStep),
			this.axisStep * Math.ceil(props.maxValue / this.axisStep)
		];

		if (props.minValue - this.axisDomain[0] < 0.66 * this.axisStep) {
			this.axisDomain[0] -= this.axisStep;
		}

		if (this.axisDomain[1] - props.maxValue < 0.66 * this.axisStep) {
			this.axisDomain[1] += this.axisStep;
		}

		this.axisScale = scaleLinear()
			.domain(this.axisDomain)
			.range([
				this.height - this.margin - 2 * this.bulbRadius,
				this.margin + 0.5 * this.tubeWidth
			]);
	}

}

export default Thermometer;



// WEBPACK FOOTER //
// ./components/thermometer/index.js